import type {
  DhlDistanceResult,
  UniuniDistanceResult,
} from "@redotech/admin-sdk/rpc/schema/rate-tables/get-carrier-distribution-center-distances";
import type { RateGrid } from "@redotech/admin-sdk/rpc/schema/rate-tables/rate-grid-schema";
import type {
  AmazonEligibility,
  UpsEligibility,
  UspsEligibility,
} from "@redotech/redo-fulfillment-carrier-common/carrier-eligibility";
import type {
  DhlEcsDistributionCenter,
  UniUniDistributionCenter,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import {
  PricingStrategy,
  SpreadUnit,
} from "@redotech/redo-fulfillment-outbound-labels-common/carrier-quote";
import { RateTableCarrier } from "@redotech/redo-fulfillment-outbound-labels-common/rate-table-carrier";
import type { RedoCarrierRateTables } from "@redotech/redo-fulfillment-rate-table-common/global-rate-tables";
import {
  type DhlSettings,
  MissingMerchantRateMode,
  RATE_CALC_CARRIERS,
  type RevShareSettings,
} from "@redotech/redo-fulfillment-rate-table-common/pricing-calculator";

export type WizardStep = 1 | 2 | 3;

export type DataSource = "upload" | "historical" | "usps_market_rates";

export type RateCalcCarrier = (typeof RATE_CALC_CARRIERS)[number];

export interface ServiceLevelStats {
  Standard: number;
  Plus: number;
  Express: number;
}

export interface Address {
  street1: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface DhlEligibility {
  eligible: boolean;
  nearestDC: DhlEcsDistributionCenter;
  distanceMiles: number;
  allDistances: DhlDistanceResult[];
}

export interface UniUniEligibility {
  eligible: boolean;
  nearestDC: UniUniDistributionCenter;
  distanceMiles: number;
  allDistances: UniuniDistanceResult[];
}

export interface CarrierEligibility {
  dhl: DhlEligibility | null;
  amazon: AmazonEligibility;
  ups: UpsEligibility;
  usps: UspsEligibility;
  uniuni: UniUniEligibility | null;
}

export interface QuoteCell {
  weightOz: number;
  zone: string;
  merchantRate: number | null;
  redoBaseRate: number | null;
  quotedRate: number | null;
  savings: number | null;
  winningCarrier: RateCalcCarrier | null;
  orderCount: number;
}

export interface CarrierBreakdown {
  carrier: RateCalcCarrier;
  ordersWon: number;
  ordersPct: number;
  totalSavings: number;
  totalDealSize: number;
  totalSpread: number;
}

export interface QuoteResults {
  cells: QuoteCell[];
  summary: {
    totalOrders: number;
    estMerchantSavings: number;
    estRedoDealSize: number;
    totalSpread: number;
    ordersWonPct: number;
    avgOrderPrice: number;
    avgSpreadPerOrder: number;
  };
  carrierBreakdown: CarrierBreakdown[];
}

export interface RateCalculatorState {
  step: WizardStep;
  isOpen: boolean;
  sessionName: string;

  baselineRateTables: RedoCarrierRateTables | null;
  baselineRateTablesLoading: boolean;
  baselineRateTablesError: string | null;

  dataSource: DataSource;
  rateGrid: RateGrid | null;
  serviceLevelStats: ServiceLevelStats | null;
  totalShipments: number;
  oldestShipmentDate: string | null;
  newestShipmentDate: string | null;
  dataError: string | null;
  uploadedFile: File | null;
  uploadedFileUrl: string | null;
  destinationZips: string[] | null;

  merchantAddress: Address | null;
  dailyVolume: number;
  carrierEligibility: CarrierEligibility | null;
  selectedCarriers: RateCalcCarrier[];
  overriddenCarriers: RateCalcCarrier[];
  eligibilityLoading: boolean;

  pricingStrategy: PricingStrategy;
  unit: SpreadUnit;
  amount: number;
  missingMerchantRateMode: MissingMerchantRateMode;
  defaultSpreadNoMerchantRate: number;
  defaultSpreadUnit: SpreadUnit;
  dhlSettings: DhlSettings;
  revShareSettings: RevShareSettings;

  quoteResults: QuoteResults | null;
}

export type RateCalculatorAction =
  | { type: "OPEN_WIZARD" }
  | { type: "CLOSE_WIZARD" }
  | { type: "SET_STEP"; step: WizardStep }
  | { type: "SET_BASELINE_RATE_TABLES"; rateTables: RedoCarrierRateTables }
  | { type: "SET_BASELINE_RATE_TABLES_LOADING"; loading: boolean }
  | { type: "SET_BASELINE_RATE_TABLES_ERROR"; error: string | null }
  | { type: "SET_DATA_SOURCE"; source: DataSource }
  | {
      type: "SET_RATE_DATA";
      rateGrid: RateGrid;
      serviceLevelStats: ServiceLevelStats | null;
      totalShipments: number;
      oldestShipmentDate: string | null;
      newestShipmentDate: string | null;
      destinationZips?: string[] | null;
      uploadedFile?: File | null;
      uploadedFileUrl?: string | null;
    }
  | { type: "SET_DATA_ERROR"; error: string | null }
  | { type: "SET_UPLOADED_FILE"; file: File | null }
  | { type: "SET_DESTINATION_ZIPS"; zips: string[] | null }
  | { type: "SET_MERCHANT_ADDRESS"; address: Address }
  | { type: "SET_DAILY_VOLUME"; volume: number }
  | { type: "SET_CARRIER_ELIGIBILITY"; eligibility: CarrierEligibility }
  | { type: "SET_ELIGIBILITY_LOADING"; loading: boolean }
  | { type: "TOGGLE_CARRIER"; carrier: RateCalcCarrier }
  | { type: "TOGGLE_CARRIER_OVERRIDE"; carrier: RateCalcCarrier }
  | { type: "SET_SESSION_NAME"; name: string }
  | { type: "SET_PRICING_STRATEGY"; strategy: PricingStrategy }
  | { type: "SET_UNIT"; unit: SpreadUnit }
  | { type: "SET_AMOUNT"; amount: number }
  | { type: "SET_MISSING_MERCHANT_RATE_MODE"; mode: MissingMerchantRateMode }
  | { type: "SET_DEFAULT_SPREAD_NO_MERCHANT_RATE"; amount: number }
  | { type: "SET_DEFAULT_SPREAD_UNIT"; unit: SpreadUnit }
  | { type: "SET_DHL_SETTINGS"; settings: DhlSettings }
  | { type: "SET_REV_SHARE_SETTINGS"; settings: RevShareSettings }
  | { type: "SET_QUOTE_RESULTS"; results: QuoteResults }
  | { type: "RESTORE_SESSION"; state: Partial<RateCalculatorState> }
  | { type: "RESET" };

export type ColorMode = "heatmap" | "carrier";

export const CARRIER_COLORS: Record<RateCalcCarrier, string> = {
  [RateTableCarrier.UPS]: "hsl(30, 75%, 85%)",
  [RateTableCarrier.USPS]: "hsl(210, 65%, 85%)",
  [RateTableCarrier.Amazon]: "hsl(40, 80%, 82%)",
  [RateTableCarrier.UNIUNI]: "hsl(160, 55%, 82%)",
  [RateTableCarrier.DHL_ECS]: "hsl(50, 75%, 85%)",
};

export interface MixedModeCell {
  weightOz: number;
  zone: string;
  merchantRate: number | null;
  winningCarrier: RateCalcCarrier | null;
  carrierRate: number | null;
  quotedRate: number | null;
  savings: number | null;
  spread: number | null;
  orderCount: number;
}

export interface MixedModeResults {
  cells: MixedModeCell[][];
  carrierBreakdown: CarrierBreakdown[];
  summary: {
    totalOrders: number;
    ordersWon: number;
    ordersWonPct: number;
    totalSavings: number;
    totalSpread: number;
    totalDealSize: number;
    annualOrders: number;
    annualSavings: number;
    annualSpread: number;
    annualDealSize: number;
    avgOrderPrice: number;
    avgSpreadPerOrder: number;
  };
}
